import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import {
  Badge,
  Box,
  Button,
  Collapse,
  Container,
  Divider,
  Flex,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Spinner,
  Stat,
  StatHelpText,
  StatLabel,
  StatNumber,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  VStack,
  FormControl,
  FormLabel,
  Input,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  useDisclosure,
} from "@chakra-ui/react";
import { ChevronDownIcon, ChevronRightIcon, EditIcon } from "@chakra-ui/icons";
import api from "./api";

interface SeasonKpi {
  participants: number;
  questsClaimed: number;
  ticketsIssued: number;
  spinsTotal: number;
  prizeDistributed: number;
  snapshotAt: string;
}

interface SeasonStat {
  _id: string;
  seasonId: string;
  label: string;
  startsAt: string;
  endsAt: string;
  active: boolean;
  prizePool: number;
  raffleEnabled: boolean;
  blocked: boolean;
  maxLevel: number;
  xpPerLevel: number;
  kpi?: SeasonKpi;
  // resolved (from kpi or live aggregation)
  participants: number;
  questsClaimed: number;
  ticketsIssued: number;
  spinsTotal: number;
  prizeDistributed: number;
}

function fmt(n: number | undefined | null) {
  return (n ?? 0).toLocaleString("en-US");
}

function fmtUsd(n: number | undefined | null) {
  return `$${(n ?? 0).toLocaleString("en-US", { maximumFractionDigits: 2 })}`;
}

function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function StatCard({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <Box bg="gray.700" borderRadius="lg" p={4}>
      <Stat>
        <StatLabel color="gray.400" fontSize="xs" textTransform="uppercase" letterSpacing="wider">
          {label}
        </StatLabel>
        <StatNumber color="white" fontSize="xl" fontWeight="bold">
          {value}
        </StatNumber>
        {sub && <StatHelpText color="gray.500" fontSize="xs" mb={0}>{sub}</StatHelpText>}
      </Stat>
    </Box>
  );
}

function KpiDetail({ s }: { s: SeasonStat }) {
  const isSnapped = !!s.kpi?.snapshotAt;
  return (
    <Box bg="gray.900" px={6} py={5} borderTop="1px solid" borderColor="gray.700">
      <Flex align="center" gap={3} mb={4}>
        <Text color="gray.300" fontWeight="semibold" fontSize="sm">
          KPI Breakdown — {s.label}
        </Text>
        {isSnapped ? (
          <Badge colorScheme="green" fontSize="10px" px={2} borderRadius="full">
            Stored {new Date(s.kpi!.snapshotAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
          </Badge>
        ) : (
          <Badge colorScheme="yellow" fontSize="10px" px={2} borderRadius="full">
            Live — saved automatically when season ends
          </Badge>
        )}
      </Flex>
      <SimpleGrid columns={{ base: 2, sm: 3, md: 5 }} spacing={3}>
        <KpiCard label="Participants" value={fmt(s.participants)} color="blue.300" />
        <KpiCard label="Quests Claimed" value={fmt(s.questsClaimed)} color="purple.300" />
        <KpiCard label="Tickets Issued" value={fmt(s.ticketsIssued)} color="cyan.300" />
        <KpiCard label="Raffle Spins" value={fmt(s.spinsTotal)} color="orange.300" />
        <KpiCard label="Prize Distributed" value={fmtUsd(s.prizeDistributed)} color="green.300" />
      </SimpleGrid>
      <Divider mt={4} borderColor="gray.700" />
      <Flex mt={3} gap={6} wrap="wrap">
        <Text color="gray.500" fontSize="xs">Prize Pool: <Text as="span" color="yellow.300" fontWeight="bold">{fmtUsd(s.prizePool)}</Text></Text>
        <Text color="gray.500" fontSize="xs">Season ID: <Text as="span" color="gray.400">#{s.seasonId}</Text></Text>
        <Text color="gray.500" fontSize="xs">Period: <Text as="span" color="gray.400">{fmtDate(s.startsAt)} → {fmtDate(s.endsAt)}</Text></Text>
        {s.maxLevel && <Text color="gray.500" fontSize="xs">Max Level: <Text as="span" color="gray.400">{s.maxLevel}</Text></Text>}
        {s.xpPerLevel && <Text color="gray.500" fontSize="xs">XP / Level: <Text as="span" color="gray.400">{s.xpPerLevel}</Text></Text>}
      </Flex>
    </Box>
  );
}

function KpiCard({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <Box bg="gray.800" borderRadius="lg" p={4} border="1px solid" borderColor="gray.700">
      <Text color="gray.400" fontSize="10px" textTransform="uppercase" letterSpacing="wider" mb={1}>
        {label}
      </Text>
      <Text color={color} fontSize="2xl" fontWeight="bold" lineHeight={1}>
        {value}
      </Text>
    </Box>
  );
}

export default function Seasons() {
  const [seasons, setSeasons] = useState<SeasonStat[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<SeasonStat | null>(null);
  const [editing, setEditing] = useState<Partial<SeasonStat>>({});
  const [saving, setSaving] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const load = () => {
    setLoading(true);
    api
      .get("/api/arena/seasons")
      .then((res) => setSeasons(res.data.data ?? []))
      .catch(() => toast.error("Failed to load seasons"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openEdit = (s: SeasonStat) => {
    setSelected(s);
    setEditing({ label: s.label, prizePool: s.prizePool, endsAt: s.endsAt.slice(0, 10) });
    onOpen();
  };

  const save = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await api.patch("/api/arena/season", {
        seasonId: selected.seasonId,
        label: editing.label,
        prizePool: editing.prizePool,
        endsAt: editing.endsAt,
      });
      toast.success("Season updated");
      onClose();
      load();
    } catch {
      toast.error("Failed to update season");
    } finally {
      setSaving(false);
    }
  };

  // Totals across all seasons
  const totals = seasons.reduce(
    (acc, s) => ({
      participants: acc.participants + s.participants,
      questsClaimed: acc.questsClaimed + s.questsClaimed,
      ticketsIssued: acc.ticketsIssued + s.ticketsIssued,
      spinsTotal: acc.spinsTotal + s.spinsTotal,
      prizeDistributed: acc.prizeDistributed + s.prizeDistributed,
      prizePool: acc.prizePool + s.prizePool,
    }),
    { participants: 0, questsClaimed: 0, ticketsIssued: 0, spinsTotal: 0, prizeDistributed: 0, prizePool: 0 }
  );

  return (
    <Container maxW="container.xl" py={8}>
      <Flex justify="space-between" align="center" mb={6}>
        <Box>
          <Heading size="lg" color="white">Seasons</Heading>
          <Text color="gray.400" mt={1} fontSize="sm">
            Historical data across all Arena seasons
          </Text>
        </Box>
        <Button size="sm" colorScheme="brand" onClick={load} isLoading={loading}>
          Refresh
        </Button>
      </Flex>

      {/* All-time totals */}
      {!loading && seasons.length > 0 && (
        <SimpleGrid columns={{ base: 2, md: 3, lg: 6 }} spacing={3} mb={8}>
          <StatCard label="Seasons" value={fmt(seasons.length)} />
          <StatCard label="Total Prize Pool" value={fmtUsd(totals.prizePool)} />
          <StatCard label="Prize Distributed" value={fmtUsd(totals.prizeDistributed)} />
          <StatCard label="Unique Participants" value={fmt(totals.participants)} sub="per-season sum" />
          <StatCard label="Quests Claimed" value={fmt(totals.questsClaimed)} />
          <StatCard label="Raffle Spins" value={fmt(totals.spinsTotal)} />
        </SimpleGrid>
      )}

      {/* Seasons table */}
      <Box bg="gray.800" borderRadius="xl" border="1px solid" borderColor="gray.700" overflow="hidden">
        {loading ? (
          <Flex justify="center" align="center" py={16}>
            <VStack>
              <Spinner color="brand.400" size="lg" />
              <Text color="gray.400" fontSize="sm">Loading seasons…</Text>
            </VStack>
          </Flex>
        ) : seasons.length === 0 ? (
          <Flex justify="center" align="center" py={16}>
            <Text color="gray.500">No seasons found</Text>
          </Flex>
        ) : (
          <Table variant="simple" size="sm">
            <Thead bg="gray.750">
              <Tr>
                <Th color="gray.400" borderColor="gray.700" width="28px" />
                <Th color="gray.400" borderColor="gray.700">Season</Th>
                <Th color="gray.400" borderColor="gray.700">Dates</Th>
                <Th color="gray.400" borderColor="gray.700" isNumeric>Prize Pool</Th>
                <Th color="gray.400" borderColor="gray.700" isNumeric>Participants</Th>
                <Th color="gray.400" borderColor="gray.700" isNumeric>Quests</Th>
                <Th color="gray.400" borderColor="gray.700" isNumeric>Tickets</Th>
                <Th color="gray.400" borderColor="gray.700" isNumeric>Spins</Th>
                <Th color="gray.400" borderColor="gray.700" isNumeric>Distributed</Th>
                <Th color="gray.400" borderColor="gray.700" width="60px" />
              </Tr>
            </Thead>
            <Tbody>
              {seasons.map((s) => {
                const expanded = expandedId === s.seasonId;
                return (
                  <>
                    <Tr
                      key={s.seasonId}
                      cursor="pointer"
                      _hover={{ bg: "gray.750" }}
                      bg={s.active ? "brand.900" : undefined}
                      onClick={() => setExpandedId(expanded ? null : s.seasonId)}
                    >
                      <Td borderColor="gray.700" py={3} px={3}>
                        {expanded
                          ? <ChevronDownIcon color="gray.400" boxSize={4} />
                          : <ChevronRightIcon color="gray.600" boxSize={4} />}
                      </Td>
                      <Td borderColor="gray.700" py={3}>
                        <Flex align="center" gap={2}>
                          <Text color="white" fontWeight={s.active ? "bold" : "normal"}>
                            {s.label}
                          </Text>
                          {s.active && (
                            <Badge colorScheme="green" fontSize="10px" px={2} borderRadius="full">
                              Active
                            </Badge>
                          )}
                          {s.blocked && (
                            <Badge colorScheme="red" fontSize="10px" px={2} borderRadius="full">
                              Blocked
                            </Badge>
                          )}
                          {s.kpi?.snapshotAt && (
                            <Badge colorScheme="blue" fontSize="10px" px={2} borderRadius="full">
                              KPI ✓
                            </Badge>
                          )}
                        </Flex>
                        <Text color="gray.500" fontSize="11px">#{s.seasonId}</Text>
                      </Td>
                      <Td borderColor="gray.700" py={3}>
                        <Text color="gray.300" fontSize="xs">
                          {fmtDate(s.startsAt)} →
                        </Text>
                        <Text
                          color={s.active && new Date(s.endsAt) < new Date() ? "red.400" : "gray.300"}
                          fontSize="xs"
                        >
                          {fmtDate(s.endsAt)}
                        </Text>
                      </Td>
                      <Td borderColor="gray.700" isNumeric>
                        <Text color="yellow.300" fontWeight="bold">{fmtUsd(s.prizePool)}</Text>
                      </Td>
                      <Td borderColor="gray.700" isNumeric>
                        <Text color="gray.200">{fmt(s.participants)}</Text>
                      </Td>
                      <Td borderColor="gray.700" isNumeric>
                        <Text color="gray.200">{fmt(s.questsClaimed)}</Text>
                      </Td>
                      <Td borderColor="gray.700" isNumeric>
                        <Text color="gray.200">{fmt(s.ticketsIssued)}</Text>
                      </Td>
                      <Td borderColor="gray.700" isNumeric>
                        <Text color="gray.200">{fmt(s.spinsTotal)}</Text>
                      </Td>
                      <Td borderColor="gray.700" isNumeric>
                        <Text color={s.prizeDistributed > 0 ? "green.300" : "gray.500"}>
                          {fmtUsd(s.prizeDistributed)}
                        </Text>
                      </Td>
                      <Td borderColor="gray.700" onClick={(e) => e.stopPropagation()}>
                        <Tooltip label="Edit season" placement="left">
                          <Button
                            size="xs"
                            variant="ghost"
                            color="gray.400"
                            _hover={{ color: "white", bg: "gray.700" }}
                            onClick={() => openEdit(s)}
                          >
                            <EditIcon />
                          </Button>
                        </Tooltip>
                      </Td>
                    </Tr>
                    {expanded && (
                      <Tr key={`${s.seasonId}-kpi`}>
                        <Td colSpan={10} p={0} borderColor="gray.700">
                          <Collapse in={expanded} animateOpacity>
                            <KpiDetail s={s} />
                          </Collapse>
                        </Td>
                      </Tr>
                    )}
                  </>
                );
              })}
            </Tbody>
          </Table>
        )}
      </Box>

      {/* Edit modal */}
      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent bg="gray.800" borderColor="gray.700">
          <ModalHeader color="white">
            Edit Season{selected ? ` — ${selected.label}` : ""}
          </ModalHeader>
          <ModalCloseButton color="gray.400" />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <FormControl>
                <FormLabel color="gray.400" fontSize="sm">Label</FormLabel>
                <Input
                  value={editing.label ?? ""}
                  onChange={(e) => setEditing((p) => ({ ...p, label: e.target.value }))}
                  bg="gray.900"
                  borderColor="gray.600"
                  color="white"
                  _focus={{ borderColor: "brand.500" }}
                />
              </FormControl>

              <FormControl>
                <FormLabel color="gray.400" fontSize="sm">Prize Pool (USD)</FormLabel>
                <NumberInput
                  value={editing.prizePool ?? 0}
                  onChange={(_, v) => setEditing((p) => ({ ...p, prizePool: isNaN(v) ? 0 : v }))}
                  min={0}
                  step={500}
                >
                  <NumberInputField bg="gray.900" borderColor="gray.600" color="white" _focus={{ borderColor: "brand.500" }} />
                  <NumberInputStepper>
                    <NumberIncrementStepper borderColor="gray.600" color="gray.400" />
                    <NumberDecrementStepper borderColor="gray.600" color="gray.400" />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>

              <FormControl>
                <FormLabel color="gray.400" fontSize="sm">End Date</FormLabel>
                <Input
                  type="date"
                  value={typeof editing.endsAt === "string" ? editing.endsAt.slice(0, 10) : ""}
                  onChange={(e) => setEditing((p) => ({ ...p, endsAt: e.target.value }))}
                  bg="gray.900"
                  borderColor="gray.600"
                  color="white"
                  _focus={{ borderColor: "brand.500" }}
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter gap={2}>
            <Button variant="ghost" color="gray.400" onClick={onClose}>Cancel</Button>
            <Button colorScheme="brand" onClick={save} isLoading={saving} loadingText="Saving…">
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Container>
  );
}
